import { isEmpty, maxBy, omit, pick, uniq } from 'lodash/fp';

import { MONTH_COUNT_KEY, ROUND_ID_KEY } from '@portal/common/models/data-key';
import { DATA_PROJECTS_WITH_ALL_DATA_TYPES_OPTION } from '@portal/common/models/data-project';
import {
    AllDataTypes,
    COMPATIBLE_DATA_TYPE_GROUPS,
    dataTypeHasPrimaryEntity,
    getDataTypePrimaryEntityKey,
} from '@portal/common/models/data-type';
import { getDataFilterToDataTypeMap } from '@portal/common/models/data-type-config';
import {
    DataCollectionResource,
    DataCondition,
    DataFilters,
    DataGranularityByType,
    DataResponse,
    DataType,
    DataValue,
    Organization,
} from '@portal/common/types';
import { mergeGranularity } from '@portal/common/utility/merge-granularity';

import api from '../api';
import { intersectRefinementFiltersWithGranularity } from '../components/DataExplorer/utils';
import config from '../config';
import createCachedRequest from './data/create-cached-request';

type LoadDataOptions = {
    skipCache?: boolean;
};

const MAX_REQUEST_CACHE_SIZE = 5;

type DataChunkRequestParams = {
    // key-only: scopes the cache per organization
    organizationId: number;
    dataCollectionId: number;
    dataType: DataType;
    filters: DataFilters;
};

const fetchDataChunk = createCachedRequest(
    ({ dataCollectionId, dataType, filters }: DataChunkRequestParams) =>
        api.data.queryDataCollectionResourceData(dataCollectionId, dataType, filters),
    MAX_REQUEST_CACHE_SIZE
);

interface ChunkResult {
    key: string;
    chunks: any[][];
    remainingFilters: DataFilters;
}

const splitArrayToChunks = <T,>(value: T[], n: number): T[][] => {
    const result: T[][] = [];

    for (let i = 0; i < value.length; i += n) {
        result.push(value.slice(i, i + n));
    }

    return result;
};

export const countFilterCombinations = (filters: DataFilters): number =>
    Object.entries(filters || {})
        .map(([, value]) => (value || []).length)
        .reduce((total, num) => total * num, 1);

const chunkLargeFilter = (filters: DataFilters, limit: number): ChunkResult | null => {
    const filtersSelectionCount = countFilterCombinations(filters);

    if (filtersSelectionCount <= limit) {
        return null;
    }

    // Find the filter with the most values
    const [maxKey, maxValues] = maxBy(([, value]) => value.length, Object.entries(filters));

    // Calculate optimal chunk size
    const chunkSize = Math.ceil(
        Math.min(
            // Ensure chunks result in selections under limit
            (limit * maxValues.length) / filtersSelectionCount + 1,
            // Never chunk larger than half the array
            maxValues.length / 2
        )
    );

    return {
        key: maxKey,
        chunks: splitArrayToChunks(maxValues, chunkSize),
        remainingFilters: omit([maxKey], filters),
    };
};

const omitEmptyFilters = (filters: DataFilters): DataFilters =>
    Object.fromEntries(
        Object.entries(filters).filter(([, value]) => !isEmpty(value))
    ) as DataFilters;

const splitFiltersToFitLimit = (filters: DataFilters, limit: number): DataFilters[] => {
    const result: DataFilters[] = [];
    const filtersToProcess: DataFilters[] = [filters];

    while (filtersToProcess.length > 0) {
        const currentFilters = filtersToProcess.pop();
        const chunking = chunkLargeFilter(currentFilters, limit);

        if (!chunking) {
            // If no chunking needed, add filters as is
            result.push(currentFilters);
            continue;
        }

        // Create new filter sets with chunked values
        const { key, chunks, remainingFilters } = chunking;
        chunks.forEach((chunk) => {
            filtersToProcess.push({
                ...remainingFilters,
                [key]: chunk,
            });
        });
    }

    return result;
};

const mergeDataResponses = (responses: DataResponse[]): DataResponse => {
    const merged: DataResponse = {};
    responses.forEach((responseByDataType) => {
        const [dataType, response] = Object.entries(responseByDataType)[0];
        const { columns, records } = response as DataResponse;
        if (!merged[dataType]) {
            merged[dataType] = { columns, records: [] };
        }
        merged[dataType].records = merged[dataType].records.concat(records);
    });
    return merged;
};

// the single definition of a request's shape, so limit gates cannot desync
export const buildDataTypeRequestFilters = (
    dataCollection: DataCollectionResource,
    dataType: DataType,
    dataTypeConditions: DataCondition[],
    filters: DataFilters,
    granularityOverrides: DataFilters,
    customFilters: DataFilters = {}
): DataFilters | null => {
    const dataTypeDataset = dataCollection?.datasets?.find(
        ({ data_type }) => data_type === dataType
    );
    if (!dataTypeDataset) {
        return null;
    }

    const { primary_entity_id_filter: primaryEntityFilter, granularity: datasetGranularity } =
        dataTypeDataset;
    const datasetGranularityKeys = Object.keys(datasetGranularity);

    const emptyGranularityOverrides = Object.fromEntries(
        Object.entries(datasetGranularity)
            .filter(([, value]) => isEmpty(value))
            .map(([key]) => [key, [] as DataValue])
    );

    const granularity = {
        ...pickByGranularityKeys(granularityOverrides, datasetGranularityKeys),
        [primaryEntityFilter]: datasetGranularity[primaryEntityFilter],
        ...emptyGranularityOverrides,
    };

    const refinementFiltersToIntersect = pickByGranularityKeys(
        {
            ...filters,
            ...conditionsToDataFilters(dataTypeConditions),
            ...customFilters,
        },
        datasetGranularityKeys
    );

    return omitEmptyFilters({
        ...granularity,
        ...intersectRefinementFiltersWithGranularity(refinementFiltersToIntersect, granularity),
    });
};

export const loadData =
    (
        organization: Organization,
        dataCollection: DataCollectionResource,
        conditions: DataCondition[],
        customFilters: DataFilters = {},
        customGranularity = null,
        options: LoadDataOptions = {}
    ) =>
    (filters: DataFilters): Promise<DataResponse> => {
        const { id: organizationId } = organization;
        const { skipCache = false } = options;
        const dataTypes = conditions ? uniq(conditions.map(({ data_type }) => data_type)) : null;

        if (!dataTypes) {
            return Promise.resolve(null);
        }

        const mergedGranularity =
            customGranularity ?? getMergedDataCollectionGranularity(dataCollection, dataTypes);

        return Promise.all(
            (dataTypes ?? [])
                .map((dataType) => {
                    const requestFilters = buildDataTypeRequestFilters(
                        dataCollection,
                        dataType,
                        conditions.filter(
                            (condition: DataCondition) => condition.data_type === dataType
                        ),
                        filters,
                        mergedGranularity,
                        customFilters
                    );

                    if (!requestFilters) {
                        return [];
                    }

                    return splitFiltersToFitLimit(
                        requestFilters,
                        config.combinedFiltersCountPerQueryLimit
                    ).map((filters) =>
                        fetchDataChunk(
                            {
                                organizationId,
                                dataCollectionId: dataCollection.id,
                                dataType,
                                filters,
                            },
                            { skipCache }
                        )
                    );
                })
                .flat()
        ).then(mergeDataResponses);
    };

export const getMergedDataCollectionGranularity = (
    dataCollection?: DataCollectionResource,
    dataTypes: DataType[] = []
) => {
    if (!dataCollection || isEmpty(dataTypes)) {
        return {};
    }

    // data types sharing a hierarchy are union so they can be queried together
    const conditionTypesToMerge: DataType[] = (
        COMPATIBLE_DATA_TYPE_GROUPS.find((group) =>
            group.some((dataType) => dataTypes.includes(dataType))
        ) || []
    ).filter((dataType: DataType) => dataTypes.includes(dataType));

    if (conditionTypesToMerge.length > 1) {
        const [firstType, ...restTypes] = conditionTypesToMerge;
        const baseGranularity = getGranularityByDataType(dataCollection, firstType);

        if (!baseGranularity) {
            return {};
        }

        return mergeGranularity(
            baseGranularity,
            ...restTypes.map((dataType) => getGranularityByDataType(dataCollection, dataType))
        );
    }

    const dataset = dataCollection.datasets.find(({ data_type }) => data_type === dataTypes[0]);

    return dataset?.granularity ?? {};
};

export const getDataCollectionGranularityByDataTypes = (
    dataCollection?: DataCollectionResource,
    dataTypes?: DataType[]
): DataGranularityByType => {
    const granularity: DataGranularityByType = {};
    if (!dataCollection || isEmpty(dataTypes)) {
        return granularity;
    }

    (dataTypes || []).forEach((dataType) => {
        granularity[dataType] = dataCollection.datasets.find(
            ({ data_type }) => data_type === dataType
        )?.granularity;
    });

    return granularity;
};

const getGranularityByDataType = (dataCollection: DataCollectionResource, dataType: DataType) => {
    if (!dataCollection) {
        return;
    }

    return dataCollection.datasets.find(({ data_type }) => data_type === dataType)!.granularity;
};

export const dataFiltersToConditions = (filters: DataFilters): DataCondition[] => {
    const filterToDataTypeMap = getDataFilterToDataTypeMap();
    return Object.entries(filters).flatMap(([key, value]) => {
        if (isEmpty(value)) {
            return [];
        }
        const dataTypes = filterToDataTypeMap[key];
        if (!dataTypes) {
            return [];
        }
        return value.flatMap((primary_entity_id) =>
            dataTypes.map((data_type: DataType) => ({ data_type, primary_entity_id }))
        );
    });
};

/**
 * Translates an array of data conditions into a DataFilters object.
 * Each condition with a primary entity (e.g. cause, sequela) is mapped to the
 * corresponding filter key with its primary_entity_id; multiple conditions
 * for the same data type are grouped into arrays per key.
 */
export const conditionsToDataFilters = (conditions: DataCondition[]): DataFilters => {
    if (isEmpty(conditions)) {
        return {};
    }

    return conditions.reduce<DataFilters>((acc, condition) => {
        const { data_type, primary_entity_id } = condition;

        if (!dataTypeHasPrimaryEntity(data_type)) {
            return acc;
        }

        const key = getDataTypePrimaryEntityKey(data_type);
        if (acc[key] == null) {
            acc[key] = [];
        }

        (acc[key] as number[]).push(primary_entity_id);

        return acc;
    }, {});
};

// it will add selected conditions to filters
export const getFiltersWithConditions = (
    selectedConditions: DataCondition[],
    filters: DataFilters,
    granularityKeys: string[]
): DataFilters => {
    const conditionFilters = conditionsToDataFilters(selectedConditions);

    return pickByGranularityKeys(
        {
            ...filters,
            ...conditionFilters,
        },
        granularityKeys
    );
};

export const pickByGranularityKeys = (
    filters: DataFilters,
    granularityKeys: string[]
): DataFilters => pick([...granularityKeys, ROUND_ID_KEY, MONTH_COUNT_KEY], filters);

export const getDataCollectionDataTypes = (dataCollection: DataCollectionResource): DataType[] => {
    const dataTypes = dataCollection.datasets.map((dataset) => dataset.data_type);
    const uniqueDataTypes = [...new Set(dataTypes)];
    if (
        uniqueDataTypes.length > 1 &&
        DATA_PROJECTS_WITH_ALL_DATA_TYPES_OPTION.includes(dataCollection.data_project_id)
    ) {
        uniqueDataTypes.unshift(AllDataTypes);
    }
    return uniqueDataTypes;
};
